/**
 * AgentMentionsInput — textarea with @ mention dropdown.
 *
 * Typing `@` opens a two-level category → leaf dropdown. On leaf selection,
 * the `@query` text is stripped from the textarea and `onVariableSelect` is
 * called so the parent can render a chip tag. This component owns only the
 * textarea and the floating dropdown — chip rendering lives in the parent
 * so it does not disturb the flex layout of the composer row.
 *
 * Dropdown position is viewport-aware: opens upward when there is enough
 * space above the container, downward otherwise.
 */
import { ArrowNarrowLeft, ChevronRight, Cube01 } from '@untitled-ui/icons-react/build/cjs';
import React, { createElement, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FC, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';

import { type AgentMentionsInputProps, type MentionCategory, type MentionLeaf } from './agentMentionsInput.interface';

const DROPDOWN_ESTIMATED_HEIGHT = 220;

function renderItemIcon(icon: MentionCategory['icon']): ReactNode {
    if (!icon) return <Cube01 className="h-3.5 w-3.5 text-grayLight600 shrink-0" />;
    if (typeof icon === 'string') return <img src={icon} className="h-3.5 w-3.5 object-contain shrink-0" alt="" />;

    return createElement(icon, { className: 'h-3.5 w-3.5 text-grayLight600 shrink-0' });
}

const AgentMentionsInput: FC<AgentMentionsInputProps> = ({
    value,
    onChange,
    onSend,
    placeholder = 'Ask something',
    disabled = false,
    variables = [],
    inputRef: externalRef,
    className = '',
    onVariableSelect,
}) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (externalRef ?? internalRef) as RefObject<HTMLTextAreaElement>;
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Suppresses the outside-click handler while the user is pressing a dropdown item,
    // preventing a race where the document mousedown fires before React's onMouseDown.
    const dropdownMouseDownRef = useRef(false);

    const [showMention, setShowMention] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionTriggerIdx, setMentionTriggerIdx] = useState(-1);
    const [activeCategory, setActiveCategory] = useState<MentionCategory | null>(null);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [dropdownDir, setDropdownDir] = useState<'top' | 'bottom'>('top');

    const filteredCategories = useMemo(() => {
        const q = mentionQuery.toLowerCase();

        return variables.filter((c) => c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
    }, [variables, mentionQuery]);

    const mentionItems: (MentionCategory | MentionLeaf)[] = useMemo(() => (activeCategory ? activeCategory.children : filteredCategories), [activeCategory, filteredCategories]);

    useEffect(() => {
        if (!showMention || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropdownDir(spaceAbove >= DROPDOWN_ESTIMATED_HEIGHT || spaceAbove > spaceBelow ? 'top' : 'bottom');
    }, [showMention]);

    const closeMention = useCallback(() => {
        setShowMention(false);
        setMentionQuery('');
        setActiveCategory(null);
        setSelectedIdx(0);
    }, []);

    const selectLeaf = useCallback(
        (leaf: MentionLeaf, category: MentionCategory) => {
            // Strip the `@query` from the textarea — chip rendering is handled by the parent
            const before = value.slice(0, mentionTriggerIdx);
            const after = value.slice(mentionTriggerIdx + 1 + mentionQuery.length);
            onChange(`${before}${after}`.trimStart());
            closeMention();
            onVariableSelect?.(leaf, category);

            setTimeout(() => {
                const el = textareaRef.current;
                if (!el) return;
                el.selectionStart = before.length;
                el.selectionEnd = before.length;
                el.focus();
            }, 0);
        },
        [value, mentionTriggerIdx, mentionQuery, onChange, closeMention, onVariableSelect, textareaRef]
    );

    const handleItemClick = useCallback(
        (index: number) => {
            if (activeCategory) {
                selectLeaf(activeCategory.children[index] as MentionLeaf, activeCategory);
            } else {
                const cat = filteredCategories[index];
                setActiveCategory(cat);
                setSelectedIdx(0);
            }
        },
        [activeCategory, filteredCategories, selectLeaf]
    );

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            const newVal = e.target.value;
            onChange(newVal);

            const cursor = e.target.selectionStart ?? newVal.length;
            const before = newVal.slice(0, cursor);
            const lastAt = before.lastIndexOf('@');

            if (lastAt !== -1) {
                const query = before.slice(lastAt + 1);

                if (!query.includes(' ') && !query.includes('\n')) {
                    setMentionTriggerIdx(lastAt);
                    setMentionQuery(query);
                    setShowMention(true);
                    setSelectedIdx(0);

                    return;
                }
            }

            closeMention();
        },
        [onChange, closeMention]
    );

    const handleKeyDown = useCallback(
        (e: ReactKeyboardEvent) => {
            if (showMention && mentionItems.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIdx((p) => (p + 1) % mentionItems.length);

                    return;
                }

                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIdx((p) => (p - 1 + mentionItems.length) % mentionItems.length);

                    return;
                }

                if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    handleItemClick(selectedIdx);

                    return;
                }

                if (e.key === 'Escape') {
                    e.preventDefault();

                    if (activeCategory) {
                        setActiveCategory(null);
                        setSelectedIdx(0);
                    } else {
                        closeMention();
                    }

                    return;
                }
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend?.();
            }
        },
        [showMention, mentionItems.length, selectedIdx, handleItemClick, activeCategory, closeMention, onSend]
    );

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            // Bail out while the user is actively pressing a dropdown item — the
            // onMouseDown handler on each button sets this ref true and resets it in
            // a setTimeout(0) after React has processed the click.
            if (dropdownMouseDownRef.current) return;
            const t = e.target as Node;

            if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
                closeMention();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);

        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [closeMention]);

    const dropdownPositionClass = dropdownDir === 'top' ? 'absolute left-0 bottom-[calc(100%+12px)]' : 'absolute left-0 top-[calc(100%+12px)]';

    const itemBase = 'flex items-center gap-x-2 px-2 py-1.5 rounded-md hover:bg-utilityGray100 bg-white cursor-pointer w-full text-left';

    return (
        <div ref={containerRef} className={`relative flex items-end ${className}`}>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={placeholder}
                aria-label="Ask agent message"
                maxLength={4000}
                disabled={disabled}
                className="resize-none scrollbar-hide w-full border-none bg-transparent text-xs leading-[24px] font-normal placeholder:text-gray500 focus:outline-none max-h-[124px] pr-[80px] py-0 disabled:opacity-50"
            />

            {showMention && mentionItems.length > 0 && (
                <div ref={dropdownRef} className={`${dropdownPositionClass} p-1.5 rounded-lg border border-utilityGray200 bg-white shadow-[0_0_10px_3px_rgba(16,24,40,0.04)] min-w-[160px] z-10`}>
                    {activeCategory && (
                        <button
                            type="button"
                            className={`${itemBase} text-grayLight600`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                dropdownMouseDownRef.current = true;
                                setTimeout(() => {
                                    dropdownMouseDownRef.current = false;
                                }, 0);
                                setActiveCategory(null);
                                setSelectedIdx(0);
                            }}
                        >
                            <ArrowNarrowLeft className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-xs font-medium">{activeCategory.label}</span>
                        </button>
                    )}

                    {mentionItems.map((item, idx) => {
                        const hasChildren = 'children' in item && (item as MentionCategory).children?.length > 0;

                        return (
                            <button
                                key={item.id}
                                type="button"
                                className={`${itemBase} ${idx === selectedIdx ? '!bg-utilityGray100' : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    dropdownMouseDownRef.current = true;
                                    setTimeout(() => {
                                        dropdownMouseDownRef.current = false;
                                    }, 0);
                                    handleItemClick(idx);
                                }}
                                onMouseEnter={() => setSelectedIdx(idx)}
                            >
                                {renderItemIcon((item as MentionCategory).icon)}
                                <span className="flex-1 truncate text-xs leading-[1.5] font-medium text-textSecondary whitespace-nowrap">{(item as MentionCategory).label}</span>
                                {hasChildren && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray400" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AgentMentionsInput;
